import {WEEK_DAY_SORTED_LIST} from 'core/constants';
import {plural} from 'core/helpers';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {IWineShopSearchResult} from 'modules/wineshops-list/models/search';
import {IGeoposition, IMetroStation, ISchedule, IWineShop} from 'modules/wineshops-list/models';
import {
    CENTRAL_WAREHOUSE_CODE,
    WINE_SHOPS_FILTERS_CODES,
    WINE_SHOPS_STOCK_TYPES_CODES,
} from 'modules/wineshops-list/constants';
import {IProductShort, IProductStock} from 'modules/products/types';

export const getWineshopsFoundCount = (count: number, words = ['винотека', 'винотеки', 'винотек']) =>
    `${count} ${plural(count, words)}`;

export const getParsedPhone = (phone?: string): string[] => phone?.split(',').map((number) => number.trim()) || [];

export const toRadians = (degrees: number) => {
    const halfCircle = 180;
    return (degrees * Math.PI) / halfCircle;
};

export function getDistance(lonStart: string, latStart: string, lonEnd: string, latEnd: string) {
    if (!lonStart || !latStart || !lonEnd || !latEnd) {
        return 0;
    }

    const earthRadiusKm = 6371;
    const kmToMeter = 1000;

    const lat1 = toRadians(Number.parseFloat(latStart));
    const lat2 = toRadians(Number.parseFloat(latEnd));
    const lon1 = toRadians(Number.parseFloat(lonStart));
    const lon2 = toRadians(Number.parseFloat(lonEnd));

    if (Number.isNaN(lat1) || Number.isNaN(lat2) || Number.isNaN(lon1) || Number.isNaN(lon2)) {
        return 0;
    }

    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const dist = earthRadiusKm * c * kmToMeter;
    return Math.round(dist);
}

export const getWalkingTime = (lonStart: string, latStart: string, lonEnd: string, latEnd: string) => {
    if (!lonStart || !latStart || !lonEnd || !latEnd) {
        return '';
    }
    const averageStepsPerMinute = 85;

    const dist = getDistance(lonStart, latStart, lonEnd, latEnd);

    const value = Math.round(dist / averageStepsPerMinute);
    return `${value} мин`;
};

export const getGoogleMapRouteLink = (lat: string, lon: string) => {
    return `https://www.google.com/maps?saddr=My+Location&daddr=${lat},${lon}`;
};

export const isBySimple = (stockType: IWineShop['stockTypes'][number]['code']) =>
    stockType === WINE_SHOPS_STOCK_TYPES_CODES.BY_SIMPLE;

export const checkHasBarInText = (text: string) => /бар/iu.test(text);

interface IGetNearestWinoshopsProps {
    selectedAddress?: IWineShopSearchResult;
    wineshopList: IWineShop[];
}

export const getWalkTimeValue = (
    {latitude: latStart, longitude: lonStart}: IMetroStation,
    geoposition?: IGeoposition,
) => {
    if (!geoposition) {
        return '';
    }

    const {latitude, longitude} = geoposition;

    return getWalkingTime(lonStart, latStart, longitude, latitude);
};

export const getNearestWinoshops = ({selectedAddress, wineshopList}: IGetNearestWinoshopsProps) => {
    return wineshopList.filter(
        (v) =>
            1 >=
            getDistance(
                v.geoposition.longitude,
                v.geoposition.latitude,
                selectedAddress?.longitude as string,
                selectedAddress?.latitude as string,
            ),
    );
};

interface IGetRemoteWinoshopsProps {
    selectedAddress?: IWineShopSearchResult;
    wineshopList: IWineShop[];
}

export const getRemoteWinoshops = ({selectedAddress, wineshopList}: IGetRemoteWinoshopsProps) => {
    return [...wineshopList]
        .sort(
            (a, b) =>
                getDistance(
                    a.geoposition.longitude,
                    a.geoposition.latitude,
                    selectedAddress?.longitude as string,
                    selectedAddress?.latitude as string,
                ) -
                getDistance(
                    b.geoposition.longitude,
                    b.geoposition.latitude,
                    selectedAddress?.longitude as string,
                    selectedAddress?.latitude as string,
                ),
        )
        .slice(0, 2);
};

interface IGetSelectedAddressWinoshopsProps {
    selectedAddress?: IWineShopSearchResult;
    wineshopList: IWineShop[];
}

export const getSelectedAddressWineshops = ({selectedAddress, wineshopList}: IGetSelectedAddressWinoshopsProps) => {
    let wineshops = wineshopList.filter((v) => v.id === selectedAddress?.id);
    let noNearestWineshops = false;

    if (!wineshops.length) {
        wineshops = getNearestWinoshops({
            selectedAddress,
            wineshopList,
        });
    }

    if (!wineshops.length) {
        noNearestWineshops = true;

        wineshops = getRemoteWinoshops({
            selectedAddress,
            wineshopList,
        });
    }

    return {
        noNearestWineshops,
        wineshops,
    };
};

/**
 * Возвращает винотеки с полной информацией + товарами, относящиеся к переданным винотекам.
 * Фильтрует `productData.stocksByCity.stores` по совпадению `warehouseCode` со списком кодов винотек.
 *
 * @param {IProduct} productData - Данные товара с распределением остатков по складам.
 * @param {IWineShop[]} wineshops - Список винотек, к складам которых нужно отфильтровать остатки.
 * @returns {Array<{ warehouseCode: string; quantity: number }>} Список складов с остатками для указанных винотек.
 */
export const getProductStocksInWineshops = (
    productData: IProduct,
    wineshops: IWineShop[],
): {warehouseCode: string; quantity: number}[] => {
    const wineShopCodes = wineshops.map(({warehouseCode}) => warehouseCode);

    return productData.stocksByCity.stores.filter(({warehouseCode}) => {
        return wineShopCodes.some((code) => code === warehouseCode);
    });
};

/**
 * Возвращает список складов (из товара, без подробной инфы), в которых товар доступен (количество > 0).
 *
 * @param {IProduct} productData - Данные товара, для которого проверяется наличие.
 * @returns {IProductStock[]} Склады, где доступно положительное количество указанного товара.
 */
export const getProductCentralStoresWithStock = (productData: IProduct | null): IProductStock[] => {
    if (!productData) {
        return [];
    }

    // TODO: переписать на нормальный вид, без массива
    const centralStore = productData.stocksByCity?.centralStore as unknown as IProductStock;
    return (centralStore?.quantity ?? 0) ? [centralStore] : [];
};

/**
 * Возвращает список винотек, в которых товар доступен (количество > 0).
 * Сопоставляет винотеки с товарами с общим списком винотек по их `warehouseCode`.
 *
 * @param {IProduct} productData - Данные товара, для которого проверяется наличие.
 * @param {IWineShop[]} wineshops - Список винотек, среди которых нужно найти наличие.
 * @returns {IWineShop[]} Винотеки, где доступно положительное количество указанного товара.
 */
export const getWineshopsWithStock = (productData: IProduct, wineshops: IWineShop[]): IWineShop[] => {
    const productStocks = getProductStocksInWineshops(productData, wineshops);

    const productAvailableStocks = productStocks.filter(({quantity}) => {
        return 0 < quantity;
    });

    return wineshops.filter(({warehouseCode}) => {
        return productAvailableStocks.some((productStock) => productStock.warehouseCode === warehouseCode);
    });
};

/**
 * Аналог `getWineshopsWithStock` для короткой модели товара (сниппеты/листинги).
 * `IProductShort` не содержит `stocksByCity` с количеством на складе — только список
 * `storesWithStock` с id складов, поэтому фильтрация идёт по наличию склада в списке,
 * без проверки количества (бэк уже не включает в `storesWithStock` пустые склады).
 *
 * @param {IProductShort['storesWithStock']} storesWithStock - Id складов, где товар есть в наличии.
 * @param {IWineShop[]} wineshops - Список винотек, среди которых нужно найти наличие.
 * @returns {IWineShop[]} Винотеки, где доступен указанный товар.
 */
export const getWineshopsWithStockShort = (
    storesWithStock: IProductShort['storesWithStock'],
    wineshops: IWineShop[],
): IWineShop[] => {
    return wineshops.filter(({warehouseCode}) => storesWithStock.includes(warehouseCode));
};

/**
 * Возвращает список винотек, доступных для самовывоза сегодня.
 * Исключает винотеки с центральным складом и оставляет только те,
 * у которых присутствует фильтр с кодом BOOK (код to_book).
 *
 * @param {IWineShop[]} wineshops Список винотек для фильтрации
 * @returns {IWineShop[]} Отфильтрованный список винотек, доступных для самовывоза сегодня
 */
export const getWineshopsForTodayPickup = (wineshops: IWineShop[]): IWineShop[] => {
    return wineshops.filter(({filters, stockTypes}) => {
        const isCentral = stockTypes.some(({code}) => code === CENTRAL_WAREHOUSE_CODE);

        if (isCentral) {
            return false;
        }

        return filters.some(({code}) => code === WINE_SHOPS_FILTERS_CODES.BOOK);
    });
};

export const getWorkingHoursSchedule = (from: string, to?: string | null) => {
    if (!to) {
        return `с ${from}`;
    }

    return `с ${from} до ${to}`;
};

export const sortSchedule = (schedule: ISchedule[]) => {
    const list = [...schedule];

    return list.sort((a, b) => {
        const aDay = a.day.toLowerCase();
        const bDay = b.day.toLowerCase();

        return WEEK_DAY_SORTED_LIST.indexOf(aDay) - WEEK_DAY_SORTED_LIST.indexOf(bDay);
    });
};
